import React, { type ReactNode, forwardRef } from "react";
import { twMerge, twJoin } from "tailwind-merge";

import { SupText } from "../../../components/UI/Text";
import type { CommonColors } from "../../../types/colors";

export interface BadgeLabelProps {
  color?: "default" | CommonColors;
  content: ReactNode;
  className?: string;
  contentClassName?: string;
  arrowPlacement?: "top" | "bottom" | "right" | "left";
  hideArrow?: boolean;
}

const STYLES = {
  default: "bg-neutral-200 text-neutral-900",
  primary: "text-neutral-50 bg-neutral-800",
  success: "text-neutral-50 bg-success-600",
  error: "text-error-25 bg-error-600",
  info: "text-info-50 bg-info-500",
  warning: "text-warning-100 bg-warning-700",
};

const CONTAINER_STYLES = {
  bottom: "pb-1",
  top: "pt-1",
  left: "pl-1",
  right: "pr-1",
};

const ARROW_COLOR = {
  default: "text-neutral-200",
  primary: "text-neutral-800",
  success: "text-success-600",
  error: "text-error-600",
  info: "text-info-500",
  warning: "text-warning-700",
};

const ARROW_STYLES = {
  bottom: "-bottom-1 left-1/2 -translate-x-1/2",
  top: "-top-1 left-1/2 -translate-x-1/2 rotate-180",
  left: "-left-1 top-1/2 -translate-y-1/2 rotate-90",
  right: "-right-1 top-1/2 -translate-y-1/2 -rotate-90",
};

export const SupBadgeLabel = forwardRef<HTMLSpanElement, BadgeLabelProps>(
  ({ className, contentClassName, color = "primary", arrowPlacement = "bottom", content, hideArrow = false }, ref) => (
    <span ref={ref} className={twMerge("sup-badge-label inline-flex", !hideArrow && CONTAINER_STYLES[arrowPlacement], className)}>
      <SupText className={twJoin("relative leading-5 px-1.5", STYLES[color], contentClassName)} variant="caption" weight="bold" as="span">
        {content}
        {!hideArrow && (
          <svg
            className={twJoin("absolute", ARROW_COLOR[color], ARROW_STYLES[arrowPlacement])}
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="6"
            viewBox="0 0 8 6"
            fill="none"
          >
            <path d="M4 6L0.535898 0L7.4641 0L4 6Z" fill="currentColor" />
          </svg>
        )}
      </SupText>
    </span>
  )
);

SupBadgeLabel.displayName = "SupBadgeLabel";
